import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { cx } from "@/components/ui";

/** Tracés des icônes du shell mobile (24 × 24, trait). */
export const ICONS = {
  home: "M4 11.5 12 4l8 7.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z",
  box: "M21 8 12 3 3 8l9 5 9-5Zm0 0v8l-9 5-9-5V8m18 0-9 5m0 0L3 8m9 5v8",
  ticket: "M6 2h9l3 3v17H6zM15 2v3h3M9 12h6M9 16h6",
  calendar: "M4 6h16v14H4zM4 10h16M8 3v5M16 3v5",
  chat: "M4 5h16v11H8l-4 4z",
  more: "M5 12h.01M12 12h.01M19 12h.01",
  check: "M4 4h16v16H4zM8 12l3 3 5-6",
  tag: "M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9zM8 8h.01",
  truck: "M3 6h11v10H3zM14 10h4l3 3v3h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z",
  users: "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM3 21v-1a6 6 0 0 1 12 0v1M16 3.5a4 4 0 0 1 0 7M21 21v-1a6 6 0 0 0-4-5.6",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21v-1a8 8 0 0 1 16 0v1",
  network: "M12 3v6M5 21v-4h14v4M12 9a3 3 0 1 0 0 .01M5 17v-3h14v3",
  globe: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3c2.5 2.5 3.5 5.5 3.5 9s-1 6.5-3.5 9c-2.5-2.5-3.5-5.5-3.5-9s1-6.5 3.5-9Z",
  desktop: "M3 4h18v12H3zM8 20h8M12 16v4",
  logout: "M15 4h4v16h-4M10 16l4-4-4-4M14 12H4",
  key: "M15 7a4 4 0 1 1-3.9 4.9L4 19v2h3v-2h2v-2h2l1.1-1.1A4 4 0 0 1 15 7ZM16 10h.01",
  search: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20 20l-3.5-3.5",
  send: "M12 19V5M5 12l7-7 7 7",
  code: "M9 8l-5 4 5 4M15 8l5 4-5 4M13 6l-2 12",
} as const;

export type IconName = keyof typeof ICONS;

export function MobileIcon({ name, size = 20, color = "currentColor", strokeWidth = 1.8 }: { name: IconName; size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={ICONS[name]} />
    </svg>
  );
}

/** Petite carte KPI des grilles 2 × 2 mobiles (libellé, chiffre, précision). */
export function MobileStat({
  label,
  value,
  hint,
  valueClassName,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="min-w-0 rounded-ios bg-white px-[14px] py-3">
      <div className="truncate text-[13px] text-muted">{label}</div>
      <div className={cx("mt-[2px] truncate text-[20px] font-bold tabular", valueClassName)}>{value}</div>
      {hint && <div className="truncate text-[12px] text-faint">{hint}</div>}
    </div>
  );
}

/** Bouton principal pleine largeur (« + Nouvelle tâche », « + Nouvelle promo »…). */
export function MobileActionButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="min-h-[44px] rounded-ios bg-accent text-center text-[17px] font-semibold text-white active:bg-accent-strong">
      {children}
    </button>
  );
}

/** Bouton « Voir plus » des listes paginées. */
export function MoreButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="min-h-[44px] rounded-ios bg-white text-[16px] text-accent">
      {children}
    </button>
  );
}

/**
 * Page PC affichée dans le shell mobile (Accès, Profil) : défilement
 * horizontal contenu dans la page plutôt que dans tout l'écran.
 */
export function DesktopPageFrame({ children }: { children: ReactNode }) {
  return <div className="-mx-[2px] min-w-0 max-w-full overflow-x-auto px-[2px]">{children}</div>;
}

// --- Style iOS (deploy/handoff_mobile_ios) ------------------------------------------

/** Retour contextuel de la barre de navigation (« ‹ Mes boutiques », « ‹ Plus »). */
export type MobileBack = { label: string; onClick: () => void };

/** Titre, sous-titre et retour imposés par une page au shell mobile. */
export type MobileChrome = { title: string; subtitle?: string; back?: MobileBack };

export const MobileChromeContext = createContext<(c: MobileChrome | null) => void>(() => {});

/**
 * Remplace le titre de la barre (et le retour) du shell tant que la page est montée,
 * ex. la ville de la boutique ouverte sur l'accueil. `null` : titre par défaut.
 */
export function useMobileTitle(title: string | null, opts?: { subtitle?: string; back?: MobileBack }) {
  const set = useContext(MobileChromeContext);
  const backRef = useRef(opts?.back);
  backRef.current = opts?.back;
  const backLabel = opts?.back?.label;
  const subtitle = opts?.subtitle;
  useEffect(() => {
    if (title == null) return;
    set({ title, subtitle, back: backLabel ? { label: backLabel, onClick: () => backRef.current?.onClick() } : undefined });
    return () => set(null);
  }, [set, title, subtitle, backLabel]);
}

/** Titre de section iOS (petites capitales grises au-dessus d'un groupe). */
export function IosSection({
  id,
  title,
  action,
  children,
  className,
}: {
  id?: string;
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={cx("min-w-0", className)}>
      <div className="flex items-baseline gap-2 px-4 pb-[7px] text-[13px] uppercase text-muted">
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {action && <span className="flex-shrink-0 text-[15px] normal-case text-accent">{action}</span>}
      </div>
      {children}
    </section>
  );
}

/** Groupe « inset grouped » : carte blanche arrondie, lignes séparées. */
export function IosGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("ios-group overflow-hidden rounded-ios bg-white", className)}>{children}</div>;
}

/** Chevron gris des lignes navigables. */
export function IosChevron() {
  return (
    <span aria-hidden className="flex-shrink-0 text-[18px] leading-none text-ios-chevron">
      ›
    </span>
  );
}

/**
 * Ligne de liste iOS : 44 pt min, séparateur inset (masqué sur la dernière
 * ligne du groupe), chevron. Lien (`to`), bouton (`onClick`) ou simple ligne.
 */
export function IosRow({
  left,
  children,
  right,
  to,
  onClick,
  chevron = Boolean(to || onClick),
  className,
  disabled,
}: {
  left?: ReactNode;
  children: ReactNode;
  right?: ReactNode;
  to?: string;
  onClick?: () => void;
  chevron?: boolean;
  className?: string;
  disabled?: boolean;
}) {
  const cls = cx(
    "flex min-h-[44px] w-full items-center gap-3 bg-white pl-4 text-left text-ink",
    (to || onClick) && "hover:bg-[oklch(0.975_0.004_145)] active:bg-[oklch(0.95_0.004_145)] disabled:hover:bg-white disabled:active:bg-white",
    className,
  );
  const body = (
    <>
      {left}
      <span className="ios-sep flex min-w-0 flex-1 items-center gap-2 self-stretch py-[6px] pr-4">
        <span className="min-w-0 flex-1">{children}</span>
        {right}
        {chevron && <IosChevron />}
      </span>
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} className={cls}>
        {body}
      </button>
    );
  }
  return <div className={cls}>{body}</div>;
}

/** Segmented control iOS (période). */
export function IosSegmented<T extends string>({ options, value, onChange }: { options: { key: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex rounded-[9px] bg-ios-fill p-[2px]">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          className={cx(
            "flex-1 rounded-[7px] py-[6px] text-[13px] text-ink",
            value === o.key ? "bg-white font-semibold shadow-[0_3px_8px_rgba(0,0,0,0.12),0_3px_1px_rgba(0,0,0,0.04)]" : "font-medium",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Pastilles iOS défilables (boutiques, filtres) : actif en accent, inactif en gris. */
export function IosChips<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: { key: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  return (
    <div className={cx("no-scrollbar flex gap-2 overflow-x-auto", className)}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          className={cx(
            "min-h-[32px] flex-shrink-0 whitespace-nowrap rounded-full px-[14px] text-[15px] font-medium",
            value === o.key ? "bg-accent text-white" : "bg-ios-fill text-ink",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Pastille rouge de compteur (onglets, lignes de « Plus »). */
export function IosBadge({ n, className }: { n: number; className?: string }) {
  if (n <= 0) return null;
  return (
    <span
      className={cx(
        "flex h-[22px] min-w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-ios-badge px-[7px] text-[13px] font-semibold text-white",
        className,
      )}
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

/** Barre fine (4 px) des classements : fond gris iOS. */
export function IosBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="mt-[5px] h-[4px] overflow-hidden rounded-[2px] bg-ios-fill">
      <div className={cx("h-full rounded-[2px]", color)} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}
